import { useRef, useState } from "react";
import { api, type ChatResponse } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";

interface Msg { role: "user" | "assistant"; content: string; isMock?: boolean; }

const SAMPLES = [
  "ผมจะยิงโปรนี้ คุณคิดว่าอย่างไร?",
  "Lead แพงขึ้น 30% ควรทำอะไร?",
  "ผมคิดว่าจะปิด Ad Set นี้ เห็นด้วยไหม?",
  "ช่วยคิด Audience ใหม่",
];

export default function Advisor() {
  const { activeBrand } = useBrands();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    const next = [...msgs, { role: "user" as const, content }];
    setMsgs(next);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post<ChatResponse>("/ai/chat", {
        agentId: "router",
        brandId: activeBrand?.id ?? null,
        module: "advisor",
        messages: next.map((m) => ({ role: m.role, content: m.content })),
      });
      setMsgs((m) => [...m, { role: "assistant", content: r.text, isMock: r.isMock }]);
    } catch (e: any) {
      setMsgs((m) => [...m, { role: "assistant", content: "เกิดข้อผิดพลาดในการเรียก AI: " + (e?.message || "unknown") }]);
    } finally {
      setBusy(false);
      setTimeout(() => streamRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 50);
    }
  }

  return (
    <div className="page" style={{ paddingBottom: 20 }}>
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">AI Marketing Advisor</div>
          <div className="page__subtitle">
            {activeBrand ? <>บริบท: <b>{activeBrand.name}</b></> : "ยังไม่ได้เลือกแบรนด์ — เลือกจากมุมขวาบนเพื่อให้ตอบตรงบริบท"}
          </div>
        </div>
        <Icon name="Sparkles" size={22} />
      </div>

      <div className="chat">
        <div className="chat__stream" ref={streamRef}>
          {msgs.length === 0 && (
            <div className="empty" style={{ padding: "30px 10px" }}>
              <div className="empty__icon"><Icon name="Sparkles" size={28} /></div>
              <h3>ถามอะไรก็ได้เกี่ยวกับการตลาด</h3>
              <p className="mh-sec">AI จะให้ความเห็นแบบ Second Opinion — เห็นด้วย/ไม่เห็นด้วย พร้อมเหตุผล ไม่เออออตาม</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 8 }}>
                {SAMPLES.map((s) => (
                  <button key={s} className="mh-btn mh-btn--subtle mh-btn--sm" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={"bubble " + (m.role === "user" ? "bubble--user" : "bubble--ai")}>
              {m.content}
            </div>
          ))}
          {busy && <div className="bubble bubble--ai"><span className="spinner spinner--dark" /> กำลังคิด...</div>}
        </div>

        <div className="chat__composer">
          <textarea
            className="mh-textarea"
            style={{ minHeight: 52, maxHeight: 160 }}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="พิมพ์คำถาม... (Enter เพื่อส่ง, Shift+Enter ขึ้นบรรทัดใหม่)"
          />
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={() => send()} disabled={busy}>
            <Icon name="Send" size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
